import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	SIMPLIFY_NAV_NEW_USER,
	NAV_EXPERIMENT_ONBOARDING_KEYS,
	CFIND_5565_CREATION_FOCUSED_NAV_XFLOW,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useGetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import { isWithinD0Window } from '../isWithinD0Window.ts';
import { useIsEligibleForSimplifyNavXflow } from './useIsEligibleForSimplifyNavXflow.ts';

export type SimplifiedNavStyle = 'loading' | 'default-templates' | 'pin-templates' | 'control';

export type UseSimplifiedNavNewUsersResult = {
	navStyle: SimplifiedNavStyle;
	// Indicates whether the user is still within the D0 window
	isWithinD0: boolean;
	/** Which experiment path produced this result */
	source: 'new-user' | 'xflow' | null;
};

export const TREATMENT_COHORTS: Set<string> = new Set(['default-templates', 'pin-templates']);

const DEFAULT_RESULT: UseSimplifiedNavNewUsersResult = {
	navStyle: 'control',
	isWithinD0: false,
	source: null,
};

export function getOnboardingKeysToPreload(): (
	| 'creation-focused-nav:shouldShowSpotlight'
	| 'creation-focused-nav:startTime'
	| 'simplify-nav-new-user:nthUserD0SimplifiedNavEligible'
	| 'simplify-nav-new-user:nthUserD0SimplifiedNavStartAt'
	| 'creation-focused-nav-xflow:creationFocusedNavXflowCheckedEligible'
	| 'creation-focused-nav-xflow:creationFocusedNavXflowStartAt'
)[] {
	if (fg('cfind_5565_creation_focused_nav_xflow_preload')) {
		return [
			...NAV_EXPERIMENT_ONBOARDING_KEYS,
			CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.CHECKED_ELIGIBLE,
			CFIND_5565_CREATION_FOCUSED_NAV_XFLOW.START_AT,
		];
	}
	return [...NAV_EXPERIMENT_ONBOARDING_KEYS];
}

/**
 * Hook that abstracts the simplified nav experiment state for consumers.
 *
 * Onboarding states are preloaded by preloadGlobalNavigationComponent (when FG on),
 * so the useGetOnboardingState call is a cache hit in SpaceNavigation and elsewhere.
 *
 * Returns a `navStyle` that describes which nav layout to render:
 * - `'loading'` — onboarding states are still loading; hide nav items to avoid flicker
 * - `'default-templates'` — variant that hides templates (uses default visibility)
 * - `'pin-templates'` — variant that pins templates in the nav
 * - `'control'` — no customization (control, ineligible, or error)
 *
 */
export const useSimplifiedNavNewUsers = (): UseSimplifiedNavNewUsersResult => {
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		getOnboardingKeysToPreload(),
		!fg('cc_activation_simplify_nav_preloader_enabled'),
	);

	const {
		isEligible: isEligibleForSimplifyNavXflow,
		isLoading: isLoadingIsEligibleForSimplifyNavXflow,
		hasError: hasEligibleForSimplifyNavXflowError,
		isWithinD0Window: isWithinD0WindowForSimplifyNavXflow,
	} = useIsEligibleForSimplifyNavXflow({
		onboardingStateData,
		onboardingStateLoading,
		onboardingStateError,
		skip: !fg('cfind_5565_creation_focused_nav_xflow_preload'),
	});

	if (onboardingStateLoading) {
		if (fg('cc_activation_simplify_nav_preloader_enabled')) {
			return { navStyle: 'loading', isWithinD0: false, source: null };
		}
		return DEFAULT_RESULT;
	}

	if (onboardingStateError) {
		return DEFAULT_RESULT;
	}

	const entries = onboardingStateData?.onboardingState;
	if (!entries) {
		return DEFAULT_RESULT;
	}

	const eligibleEntry = entries.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_ELIGIBLE,
	);

	if (eligibleEntry?.value !== 'true') {
		// Not eligible for new user experiment, but might be for xflow.
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('cfind_5565_creation_focused_nav_xflow_preload')
		) {
			if (isLoadingIsEligibleForSimplifyNavXflow) {
				// This may happen if TAP traits are loading, although they should generally be preloaded.
				// Case of onboarding state loading is handled in previous conditionals.
				return { navStyle: 'loading', isWithinD0: false, source: null };
			}
			if (hasEligibleForSimplifyNavXflowError) {
				return DEFAULT_RESULT;
			}
			if (isEligibleForSimplifyNavXflow) {
				const navStyle = expVal(
					'cfind_5565_creation_focused_nav_xflow',
					'cohort',
					'control',
				) as SimplifiedNavStyle;
				if (TREATMENT_COHORTS.has(navStyle)) {
					return { navStyle, isWithinD0: isWithinD0WindowForSimplifyNavXflow, source: 'xflow' };
				}
			}
		}
		return DEFAULT_RESULT;
	}

	const navStyle = expVal(
		'cc_activation_simplify_nav_new_user',
		'cohort',
		'control',
	) as SimplifiedNavStyle;

	if (!TREATMENT_COHORTS.has(navStyle)) {
		return DEFAULT_RESULT;
	}

	const startAtEntry = entries.find(
		(s) => s.key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_START_AT,
	);

	return { navStyle, isWithinD0: isWithinD0Window(startAtEntry?.value), source: 'new-user' };
};
